import React, { useMemo, useState, ReactNode } from "react";
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  SearchIcon,
} from "../../../assets/icons";
import { Divider, DateRangePicker, DateRange } from "../../../components";
import { Select, SelectOption } from "../../../components/Select";
import { useGetLoanDrawdowns } from "../../../hooks/api";
import GroupTable, { ColumnDef } from "./GroupTable";
import { formatAmount } from "../../../utils";
import { DrawdownBatch } from "../../../contexts";
import {
  DrawdownStatus,
  DrawdownStatusColorMap,
  DrawdownStatusDisplayMap,
} from "../../../hooks/api/types";
import { InvoiceGroups, InvoiceListingProps } from "./constants";

const getVendorHeader = (creditLineType?: string): string => {
  return creditLineType === "RECEIVABLES" ? "Buyer Name" : "Supplier Name";
};

function buildColumns(
  groupLabel: string,
  badgeClass: string,
  creditLineType?: string
): ColumnDef<DrawdownBatch>[] {
  const statusCol: ColumnDef<DrawdownBatch> = {
    header: "Status",
    className:
      "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
    render: (dd) => (
      <span
        className={`cx-inline-flex cx-items-center cx-rounded-full cx-px-3 cx-py-1.5 cx-text-xs cx-font-medium cx-whitespace-nowrap ${
          DrawdownStatusColorMap[dd.status] || badgeClass
        }`}
      >
        {DrawdownStatusDisplayMap[dd.status]}
      </span>
    ),
  };

  const vendorHeader = getVendorHeader(creditLineType);

  const colsMap: Record<string, ColumnDef<DrawdownBatch>[]> = {
    "Action Required": [
      {
        header: "Invoice #",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.decision?.invoiceNumber || dd.invoiceNumber || "N/A",
      },
      {
        header: vendorHeader,
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.vendor?.name || "N/A",
      },
      {
        header: "Requested Amount",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd: DrawdownBatch) => getFormattedRequestedAmount(dd),
      },
      {
        header: "Approved Amount",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd: DrawdownBatch) => getFormattedApprovedAmount(dd),
      },
      statusCol,
    ],
    "In Review": [
      {
        header: "Invoice #",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.decision?.invoiceNumber || dd.invoiceNumber || "N/A",
      },
      {
        header: vendorHeader,
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.vendor?.name || "N/A",
      },
      {
        header: "Requested Amount",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd: DrawdownBatch) => getFormattedRequestedAmount(dd),
      },
      {
        header: "Invoice Due Date",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => formatDate(dd.dueDate),
      },
      statusCol,
    ],
    Approved: [
      {
        header: "Invoice #",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.decision?.invoiceNumber || dd.invoiceNumber || "N/A",
      },
      {
        header: vendorHeader,
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.vendor?.name || "N/A",
      },
      {
        header: "Approved Amount",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd: DrawdownBatch) => getFormattedApprovedAmount(dd),
      },
      {
        header: "Outstanding Amount",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd: DrawdownBatch) => getFormattedOutstandingAmount(dd),
      },
      {
        header: "Due Date",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => formatDate(dd.dueDate),
      },
      statusCol,
    ],
    Closed: [
      {
        header: "Invoice #",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.decision?.invoiceNumber || dd.invoiceNumber || "N/A",
      },
      {
        header: vendorHeader,
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.vendor?.name || "N/A",
      },
      {
        header: "Approved Amount",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd: DrawdownBatch) => getFormattedApprovedAmount(dd),
      },
      {
        header: "Due Date",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => formatDate(dd.dueDate),
      },
      statusCol,
    ],
    Rejected: [
      {
        header: "Invoice #",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.decision?.invoiceNumber || dd.invoiceNumber || "N/A",
      },
      {
        header: vendorHeader,
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => dd.vendor?.name || "N/A",
      },
      {
        header: "Requested Amount",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd: DrawdownBatch) => getFormattedRequestedAmount(dd),
      },
      {
        header: "Invoice Due Date",
        className:
          "cx-text-center cx-whitespace-nowrap cx-text-[#666] cx-font-normal",
        render: (dd) => formatDate(dd.dueDate),
      },
      statusCol,
    ],
  };

  return colsMap[groupLabel] || colsMap["In Review"];
}

const ACTION_REQUIRED_STATUSES = new Set([
  DrawdownStatus.ADDITIONAL_INFO_REQUIRED,
  DrawdownStatus.PAST_DUE,
  DrawdownStatus.PAST_MATURITY,
  DrawdownStatus.PENDING_POA_REVIEW,
]);
const IN_REVIEW_STATUSES = new Set([
  DrawdownStatus.SUBMITTED,
  DrawdownStatus.IN_REVIEW_OPS,
  DrawdownStatus.IN_REVIEW_SENIOR_OPS,
  DrawdownStatus.ON_HOLD,
]);
const APPROVED_STATUSES = new Set([
  DrawdownStatus.WAITING_DISBURSAL,
  DrawdownStatus.ACTIVE,
  DrawdownStatus.SEND_TO_LMS,
  DrawdownStatus.APPROVED,
  DrawdownStatus.OVERPAID,
]);
const CLOSED_STATUSES = new Set([
  DrawdownStatus.FORCED_CLOSURE,
  DrawdownStatus.EARLY_CLOSURE,
  DrawdownStatus.CLOSED_RESCHEDULED,
  DrawdownStatus.CLOSED_OBLIGATIONS_MET,
  DrawdownStatus.CLOSED_WRITTEN_OFF,
]);

const REJECTED_STATUSES = new Set([
  DrawdownStatus.DECLINED_OPS,
  DrawdownStatus.POA_REJECTED,
  DrawdownStatus.DISAPPROVED,
]);

const INVOICE_GROUP_TO_STATUS_MAP = {
  [InvoiceGroups.ACTION_REQUIRED]: ACTION_REQUIRED_STATUSES,
  [InvoiceGroups.IN_REVIEW]: IN_REVIEW_STATUSES,
  [InvoiceGroups.APPROVED]: APPROVED_STATUSES,
  [InvoiceGroups.CLOSED]: CLOSED_STATUSES,
  [InvoiceGroups.REJECTED]: REJECTED_STATUSES,
};

const getGroupLabel = (status?: DrawdownStatus) => {
  if (!status) return InvoiceGroups.IN_REVIEW;

  if (ACTION_REQUIRED_STATUSES.has(status))
    return InvoiceGroups.ACTION_REQUIRED;
  if (IN_REVIEW_STATUSES.has(status)) return InvoiceGroups.IN_REVIEW;
  if (APPROVED_STATUSES.has(status)) return InvoiceGroups.APPROVED;
  if (CLOSED_STATUSES.has(status)) return InvoiceGroups.CLOSED;
  if (REJECTED_STATUSES.has(status)) return InvoiceGroups.REJECTED;

  return InvoiceGroups.IN_REVIEW;
};

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "N/A";

const renderCurrencyAmount = (amount: number, currency: string): ReactNode => {
  if (currency === "AED") {
    return (
      <span className="cx-flex cx-items-center cx-justify-center">
        <span className="cx-font-aed-font">D</span>
        <span className="cx-ml-1">{formatAmount(amount)}</span>
      </span>
    );
  }
  return `${currency} ${formatAmount(amount)}`;
};

const getFormattedApprovedAmount = (dd: DrawdownBatch): ReactNode => {
  const amount = dd.approvedAmount ?? dd.decision?.fundableAmount;
  const currency = dd.currency || "AED";
  return amount ? renderCurrencyAmount(amount, currency) : "N/A";
};

const getFormattedOutstandingAmount = (dd: DrawdownBatch): ReactNode => {
  const currency = dd.currency || "AED";
  return dd.outstandingAmount
    ? renderCurrencyAmount(dd.outstandingAmount, currency)
    : "N/A";
};

const getFormattedRequestedAmount = (dd: DrawdownBatch): ReactNode => {
  const currency = dd.currency || "AED";
  return dd.requestedAmount
    ? renderCurrencyAmount(dd.requestedAmount, currency)
    : "N/A";
};

const groupByStatus = (items: DrawdownBatch[]) => {
  const groups: Record<string, DrawdownBatch[]> = {};
  items.forEach((dd) => {
    const label = getGroupLabel(dd.status);
    if (!groups[label]) groups[label] = [];
    groups[label].push(dd);
  });
  return groups;
};

const invoiceGroupColors: Record<string, string> = {
  [InvoiceGroups.ACTION_REQUIRED]: "#DC2626",
  [InvoiceGroups.IN_REVIEW]: "#08BDBA",
  [InvoiceGroups.APPROVED]: "#42BE65",
  [InvoiceGroups.CLOSED]: "#666666",
  [InvoiceGroups.REJECTED]: "#A2191F",
};

const InvoiceListing: React.FC<InvoiceListingProps> = ({
  loanApplicationId,
  initialDrawdowns,
}) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [dueDateRange, setDueDateRange] = useState<DateRange | undefined>(
    undefined
  );
  const [drawdowns, setDrawdowns] = useState<DrawdownBatch[]>(initialDrawdowns);

  const vendorHeader = getVendorHeader(drawdowns[0]?.creditLine?.type);

  const getDrawdowns = useGetLoanDrawdowns(loanApplicationId);

  const handleStatusChange = (status: string) => {
    setStatus(status);
    const groupStatus = status as InvoiceGroups;
    const ddStatuses = INVOICE_GROUP_TO_STATUS_MAP[groupStatus] || undefined;

    (async () => {
      const params = ddStatuses
        ? { statuses: Array.from(ddStatuses) }
        : undefined;
      const res = await getDrawdowns.mutateAsync(params);
      setDrawdowns(res?.drawdownBatches || res || []);
    })();
  };

  const normalizedSearch = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    return drawdowns.filter((dd) => {
      if (normalizedSearch) {
        const supplier = (dd.vendor?.name || "").toLowerCase();
        if (!supplier.includes(normalizedSearch)) return false;
      }

      if (status && status !== "All") {
        const label = getGroupLabel(dd.status);
        if (label !== status) return false;
      }

      if (dueDateRange && dd.dueDate) {
        const d = new Date(dd.dueDate);
        if (!isNaN(d.getTime())) {
          if (dueDateRange.from) {
            const start = new Date(dueDateRange.from);
            start.setHours(0, 0, 0, 0);
            if (d.getTime() < start.getTime()) return false;
          }
          if (dueDateRange.to) {
            const end = new Date(dueDateRange.to);
            end.setHours(23, 59, 59, 999);
            if (d.getTime() > end.getTime()) return false;
          }
        }
      }
      return true;
    });
  }, [drawdowns, normalizedSearch, status, dueDateRange]);

  const grouped = useMemo(() => groupByStatus(filtered), [filtered]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    [InvoiceGroups.ACTION_REQUIRED]: true,
    [InvoiceGroups.IN_REVIEW]: true,
    [InvoiceGroups.APPROVED]: true,
    [InvoiceGroups.CLOSED]: true,
    [InvoiceGroups.REJECTED]: false,
  });

  const toggle = (label: string) =>
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  const groupOrder = [
    InvoiceGroups.ACTION_REQUIRED,
    InvoiceGroups.IN_REVIEW,
    InvoiceGroups.APPROVED,
    InvoiceGroups.CLOSED,
    InvoiceGroups.REJECTED,
  ];

  const groupDisplayClasses: Record<string, string> = {
    [InvoiceGroups.IN_REVIEW]: "cx-bg-[#08BDBA] cx-text-white",
    [InvoiceGroups.ACTION_REQUIRED]: "cx-bg-error-dark cx-text-white",
    [InvoiceGroups.APPROVED]: "cx-bg-[#42BE65] cx-text-white",
    [InvoiceGroups.CLOSED]: "cx-bg-[#EEEEEE] cx-text-[#666666]",
    [InvoiceGroups.REJECTED]: "cx-bg-[#A2191F] cx-text-white",
  };

  const statusOptions: SelectOption[] = useMemo(() => {
    return Object.values(InvoiceGroups).map((group) => ({
      value: group,
      label: group,
      color: invoiceGroupColors[group],
    }));
  }, []);

  return (
    <div className="cx-mt-8">
      <div className="cx-flex cx-items-center cx-justify-between cx-mb-4">
        <h2 className="cx-text-[32px] cx-leading-9 cx-font-bold">
          All invoices
        </h2>
        <div className="cx-flex cx-items-center cx-gap-3">
          <div className="cx-relative">
            <input
              className="cx-border-2 cx-border-neutral-light cx-rounded-full cx-px-4 cx-pr-10 cx-py-0 cx-text-sm cx-text-black cx-font-normal cx-w-56 cx-min-h-8"
              placeholder={`Search by ${vendorHeader}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <SearchIcon className="cx-absolute cx-right-3 cx-top-1/2 -cx-translate-y-1/2 cx-w-4 cx-h-4 cx-text-text-secondary cx-pointer-events-none" />
          </div>
          <Select
            options={statusOptions}
            value={status}
            onChange={handleStatusChange}
            placeholder="Status"
            defaultLabel="All"
            showChevron={true}
            selectedPrefix="Status:"
            colorSelectedLabel={true}
            buttonClassName="cx-border-2 cx-border-neutral-light cx-rounded-full cx-px-4 cx-pr-3 cx-py-1 cx-text-sm cx-font-semibold cx-min-h-8 cx-appearance-none cx-bg-white cx-flex cx-items-center cx-cursor-pointer cx-text-brand-primary-regular [&>span]:cx-text-brand-primary-regular"
          />
          <div className="cx-flex cx-items-center">
            <div
              className={` cx-flex cx-items-center ${
                dueDateRange ? "cx-w-fit cx-max-w-[170px]" : "cx-w-[120px]"
              }
              cx-transition-[width]
              cx-duration-200
              `}
            >
              <DateRangePicker
                placeholderText="Due date"
                value={dueDateRange}
                onChange={(range) => setDueDateRange(range)}
                className="cx-border-2
                          cx-border-neutral-light
                          cx-bg-white
                          cx-pl-4
                          cx-pr-2
                          cx-py-1
                          cx-max-h-9
                          cx-text-brand-primary-regular
                          cx-font-semibold
                          cx-whitespace-nowrap"
                iconClassName="cx-text-brand-primary-regular"
              />
              {dueDateRange && (
                <button
                  type="button"
                  onClick={() => setDueDateRange(undefined)}
                  className="cx-text-text-secondary cx-text-base cx-font-medium cx-ml-2 hover:cx-text-error-regular"
                  aria-label="Clear date filter"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Divider className="cx-mb-4" />
      {filtered.length === 0 ? (
        <div className="cx-text-center cx-text-text-secondary cx-py-8">
          No invoices found matching the selected filters.
        </div>
      ) : (
        groupOrder
          .filter((g) => grouped[g] && grouped[g].length)
          .map((groupLabel) => (
            <div key={groupLabel} className="cx-mb-8">
              <button
                type="button"
                onClick={() => toggle(groupLabel)}
                className="cx-w-full cx-bg-white cx-rounded-lg cx-shadow-sm cx-border cx-border-neutral-light cx-px-6 cx-py-3 cx-flex cx-items-center cx-gap-3"
              >
                {expanded[groupLabel] ? (
                  <ArrowUpCircleIcon className="cx-w-6 cx-h-6 cx-text-text-secondary" />
                ) : (
                  <ArrowDownCircleIcon className="cx-w-6 cx-h-6 cx-text-text-secondary" />
                )}
                <div
                  className={
                    `cx-inline-flex cx-items-center cx-rounded-lg cx-py-2 cx-px-4 cx-text-sm cx-font-medium ` +
                    (groupDisplayClasses[groupLabel] || "")
                  }
                >
                  {groupLabel}
                </div>
              </button>

              {expanded[groupLabel] && (
                <GroupTable
                  columns={buildColumns(
                    groupLabel,
                    groupDisplayClasses[groupLabel] || "",
                    drawdowns[0]?.creditLine?.type
                  )}
                  items={grouped[groupLabel]}
                />
              )}
            </div>
          ))
      )}
    </div>
  );
};
export default InvoiceListing;
